import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  FaComments,
  FaPaperPlane,
  FaSearch,
  FaSms,
  FaWhatsapp,
} from "react-icons/fa";
import { apihubGet, apihubPost } from "../../api/apihub";
import { useProjectPhone } from "../../hooks/useProjectPhone";
import { getDisplayName, getUserName } from "../../utils/auth";

const COUNTRIES = [
  { iso: "IN", name: "India", code: "+91" },
  { iso: "US", name: "United States", code: "+1" },
  { iso: "GB", name: "United Kingdom", code: "+44" },
  { iso: "AE", name: "UAE", code: "+971" },
  { iso: "SG", name: "Singapore", code: "+65" },
];

const extractPlaceholders = (text = "") => {
  const source = String(text);
  const curly = [...source.matchAll(/\{\{(\d+)\}\}/g)].map((m) => m[1]);
  const hashNum = [...source.matchAll(/#(\d+)#/g)].map((m) => m[1]);
  const hashVar = /#var#/i.test(source) ? ["1"] : [];
  return [...new Set([...curly, ...hashNum, ...hashVar])];
};

const buildParamState = (keys) =>
  Object.fromEntries(keys.map((k) => [k, ""]));

function parseWaidToPhone(waid) {
  const digits = String(waid ?? "").replace(/\D/g, "");
  if (!digits) return null;

  const byCodeLength = [...COUNTRIES].sort(
    (a, b) => b.code.replace(/\D/g, "").length - a.code.replace(/\D/g, "").length,
  );

  for (const country of byCodeLength) {
    const codeDigits = country.code.replace(/\D/g, "");
    if (digits.startsWith(codeDigits) && digits.length > codeDigits.length) {
      return { country, mobile: digits.slice(codeDigits.length) };
    }
  }

  return { country: COUNTRIES[0], mobile: digits };
}

function buildFullPhoneNumber(country, mobile) {
  const dialCode = country?.code?.replace(/\D/g, "") || "";
  const localDigits = String(mobile ?? "").replace(/\D/g, "");
  if (!localDigits) return "";
  if (dialCode && localDigits.startsWith(dialCode)) return localDigits;
  return dialCode ? `${dialCode}${localDigits}` : localDigits;
}

const SendTestMessage = () => {
  const { projectId, phoneNoId, wabaId, projectLoading, needsWhatsAppSetup } =
    useProjectPhone();
  const { state: navState } = useLocation();
  const dropdownRef = useRef(null);

  const [channel, setChannel] = useState("whatsapp");
  const [templateSearch, setTemplateSearch] = useState("");
  const [templates, setTemplates] = useState([]);
  const [listOpen, setListOpen] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const [country, setCountry] = useState(COUNTRIES[0]);
  const [mobile, setMobile] = useState("");

  const [headerParams, setHeaderParams] = useState({});
  const [bodyParams, setBodyParams] = useState({});
  const [buttonParams, setButtonParams] = useState({});
  const [mediaUrl, setMediaUrl] = useState("");
  const [location, setLocation] = useState({
    latitude: "",
    longitude: "",
    locationName: "",
    locationAddress: "",
  });

  const [sending, setSending] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const [recipientName, setRecipientName] = useState("");

  const userName = getUserName() || getDisplayName() || "User";

  const fetchTemplates = useCallback(
    async (searchVal) => {
      if (channel === "whatsapp" && !phoneNoId && !projectId) return;
      if (channel === "sms" && !projectId) return;
      setLoadingTemplates(true);
      try {
        if (channel === "whatsapp") {
          const qs = new URLSearchParams({
            page: "1",
            limit: "20",
            sortBy: "createdAt",
            sortOrder: "desc",
            ...(searchVal ? { search: searchVal } : {}),
          });
          const phoneForPath = phoneNoId || "0";
          const res = await apihubGet(
            `/getInteractedCustomerDetail/gettotaltemplatesbyphonenumberid/${phoneForPath}?${qs}&projectId=${encodeURIComponent(
              projectId || "",
            )}`,
          );
          const list = (res?.data || []).filter(
            (t) => t.template_status === "APPROVED",
          );
          setTemplates(list);
        } else {
          const q = encodeURIComponent(searchVal || "");
          const res = await apihubGet(
            `/getInteractedCustomerDetail/smstemplatebyid/${projectId}?page=1&limit=20&q=${q}`,
          );
          const list = (res?.data || []).map((t) => ({
            ...t,
            name: t.templateName || t.name,
            category: "SMS",
            language: t.language || "en",
            sms_config_id: t.sms_config_id || t.smsConfigId || null,
            components: [
              {
                type: "BODY",
                text: t.message || t.templateMessage || "",
              },
            ],
          }));
          setTemplates(list);
        }
      } catch {
        setTemplates([]);
      } finally {
        setLoadingTemplates(false);
      }
    },
    [channel, phoneNoId, projectId],
  );

  useEffect(() => {
    if (!navState?.waid) return;
    const parsed = parseWaidToPhone(navState.waid);
    if (parsed) {
      setCountry(parsed.country);
      setMobile(String(parsed.mobile || "").replace(/\D/g, "").slice(0, 10));
    }
    if (navState.name) setRecipientName(navState.name);
  }, [navState]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (listOpen) fetchTemplates(templateSearch.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [templateSearch, listOpen, fetchTemplates]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setListOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const applyTemplate = (tpl) => {
    setSelectedTemplate(tpl);
    setTemplateSearch(tpl.name || "");
    setListOpen(false);

    const components = tpl.components || [];
    const header = components.find((c) => c.type === "HEADER");
    const body = components.find((c) => c.type === "BODY");
    const buttons = components.find((c) => c.type === "BUTTONS");

    setHeaderParams(buildParamState(extractPlaceholders(header?.text)));
    setBodyParams(buildParamState(extractPlaceholders(body?.text)));

    const btnKeys = [];
    (buttons?.buttons || []).forEach((btn) => {
      if (btn.url) btnKeys.push(...extractPlaceholders(btn.url));
    });
    setButtonParams(buildParamState([...new Set(btnKeys)]));
    setMediaUrl("");
  };

  const fillPlaceholders = (source = "", values = {}) => {
    let text = String(source || "");
    Object.entries(values).forEach(([k, v]) => {
      text = text.replace(new RegExp(`\\{\\{${k}\\}\\}`, "g"), v || `{{${k}}}`);
    });
    return text;
  };

  const previewText = () => {
    if (!selectedTemplate) return null;
    const body = selectedTemplate.components?.find((c) => c.type === "BODY");
    return fillPlaceholders(body?.text || selectedTemplate.name || "", bodyParams);
  };

  const previewHeader = () => {
    const header = selectedTemplate?.components?.find((c) => c.type === "HEADER");
    if (!header?.text) return "";
    return fillPlaceholders(header.text, headerParams);
  };

  const previewButtons = () => {
    const buttons = selectedTemplate?.components?.find((c) => c.type === "BUTTONS");
    return Array.isArray(buttons?.buttons) ? buttons.buttons : [];
  };

  const onSend = async (e) => {
    e.preventDefault();
    setApiResponse(null);

    if (!selectedTemplate) {
      setApiResponse({ success: false, message: "Please select a template." });
      return;
    }
    if (!mobile.trim()) {
      setApiResponse({ success: false, message: "Please enter a mobile number." });
      return;
    }
    if (!/^\d{10}$/.test(mobile.trim())) {
      setApiResponse({
        success: false,
        message: "Enter a 10-digit mobile number.",
      });
      return;
    }
    if (!country?.code) {
      setApiResponse({ success: false, message: "Please select a country code." });
      return;
    }
    if (channel === "whatsapp" && !phoneNoId && !projectId) {
      setApiResponse({
        success: false,
        message: "WhatsApp is not connected for this project.",
      });
      return;
    }

    const components = selectedTemplate.components || [];
    const bodyComp = components.find((c) => c.type === "BODY");

    const payload = {
      selectedCountry: country,
      selectedTemplate,
      templatename: selectedTemplate.name,
      templatecategory: selectedTemplate.category,
      templatelanguage: selectedTemplate.language,
      templatemediasheaderurl: mediaUrl,
      templatebodyvalues: bodyParams,
      templateheadervalues: headerParams,
      templatebuttonsinfo: components.find((c) => c.type === "BUTTONS"),
      templatebuttonparametersvalue: buttonParams,
      currentOpenUser: buildFullPhoneNumber(country, mobile),
      templateallcomponents: components,
      templatebodytext: bodyComp?.text,
      templatebodylocation: location,
      currentphonenumberId: phoneNoId,
      senderUserName: `${userName}-testmessage`,
      dynamicValues: {},
      mediaUrls: {},
      wabaId,
      projectId,
      channel,
    };

    setSending(true);
    setApiResponse(null);
    try {
      const res = await apihubPost(
        "/getInteractedCustomerDetail/sendtestmessage",
        payload,
      );
      setApiResponse({
        success: res?.success !== false,
        message:
          res?.message ||
          (res?.success === false
            ? "Send failed"
            : "Test message sent successfully."),
      });
    } catch (err) {
      setApiResponse({
        success: false,
        message: err.message || "Failed to send test message",
      });
    } finally {
      setSending(false);
    }
  };

  const headerKeys = Object.keys(headerParams);
  const bodyKeys = Object.keys(bodyParams);
  const buttonKeys = Object.keys(buttonParams);
  const hasParams =
    headerKeys.length > 0 || bodyKeys.length > 0 || buttonKeys.length > 0;

  const headerPreview = previewHeader();
  const buttonsPreview = previewButtons();

  return (
    <div className="sendtest-page">
      <header className="sendtest-topbar">
        <div className="sendtest-topbar-copy">
          <h1>Send Test Message</h1>
          <p>Preview a template and send it to a test number before using it in a campaign.</p>
        </div>
        {recipientName && (
          <span className="sendtest-recipient-chip">
            To {recipientName}
          </span>
        )}
      </header>

      {channel === "whatsapp" && needsWhatsAppSetup && (
        <p className="sendtest-alert sendtest-alert-error">
          Connect WhatsApp for this project before sending a test message.
        </p>
      )}

      <div className="sendtest-grid">
        <section className="sendtest-card">
          <div className="sendtest-card-head">
            <h2>Compose</h2>
            <p>Choose a channel, pick an approved template, then enter the recipient.</p>
          </div>

          <form onSubmit={onSend}>
            <div className="sendtest-field">
              <label>Channel</label>
              <div className="sendtest-channel" role="group" aria-label="Channel">
                <button
                  type="button"
                  className={`sendtest-channel-btn sendtest-channel-whatsapp${
                    channel === "whatsapp" ? " is-active" : ""
                  }`}
                  onClick={() => {
                    setChannel("whatsapp");
                    setSelectedTemplate(null);
                    setTemplateSearch("");
                    setTemplates([]);
                  }}
                >
                  <FaWhatsapp aria-hidden />
                  WhatsApp
                </button>
                <button
                  type="button"
                  className={`sendtest-channel-btn sendtest-channel-sms${
                    channel === "sms" ? " is-active" : ""
                  }`}
                  onClick={() => {
                    setChannel("sms");
                    setSelectedTemplate(null);
                    setTemplateSearch("");
                    setTemplates([]);
                  }}
                >
                  <FaSms aria-hidden />
                  SMS
                </button>
              </div>
            </div>

            <div className="sendtest-field sendtest-field-dropdown" ref={dropdownRef}>
              <label>Template</label>
              <p className="sendtest-hint">
                {channel === "sms"
                  ? "Search and select from your SMS templates."
                  : "Search and select from your approved WhatsApp templates."}
              </p>
              <div className="sendtest-search-wrap">
                <FaSearch className="sendtest-search-icon" aria-hidden />
                <input
                  type="search"
                  className="sendtest-template-search"
                  placeholder="Search template"
                  value={templateSearch}
                  onChange={(e) => {
                    setTemplateSearch(e.target.value);
                    setSelectedTemplate(null);
                  }}
                  onFocus={() => setListOpen(true)}
                />
              </div>
              {selectedTemplate && (
                <div className="sendtest-template-meta">
                  <span>{selectedTemplate.name}</span>
                  {selectedTemplate.category && (
                    <span className="sendtest-chip">{selectedTemplate.category}</span>
                  )}
                  {selectedTemplate.language && (
                    <span className="sendtest-chip">{selectedTemplate.language}</span>
                  )}
                </div>
              )}
              {listOpen && (
                <div className="sendtest-template-dropdown">
                  {loadingTemplates && (
                    <p className="sendtest-dropdown-msg">Loading templates…</p>
                  )}
                  {!loadingTemplates && templates.length === 0 && (
                    <p className="sendtest-dropdown-msg">No templates found.</p>
                  )}
                  {templates.map((tpl) => (
                    <button
                      key={tpl._id || tpl.name}
                      type="button"
                      className={`sendtest-template-option${
                        selectedTemplate?.name === tpl.name ? " is-selected" : ""
                      }`}
                      onClick={() => applyTemplate(tpl)}
                    >
                      <span className="sendtest-template-dot" />
                      <span className="sendtest-template-option-copy">
                        <strong>{tpl.name}</strong>
                        <span>
                          {[tpl.category, tpl.language].filter(Boolean).join(" · ") || "Template"}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {selectedTemplate && hasParams && (
              <div className="sendtest-params">
                <p className="sendtest-params-title">Parameters</p>
                <p className="sendtest-hint">
                  Customize messages using placeholders (e.g. {"{{1}}"}).
                </p>

                {headerKeys.length > 0 && (
                  <div>
                    <h4>Header Parameters</h4>
                    {headerKeys.map((key) => (
                      <label key={`h-${key}`}>
                        {key}
                        <input
                          value={headerParams[key]}
                          onChange={(e) =>
                            setHeaderParams((p) => ({
                              ...p,
                              [key]: e.target.value,
                            }))
                          }
                          placeholder={`Value for {{${key}}}`}
                        />
                      </label>
                    ))}
                  </div>
                )}

                {bodyKeys.length > 0 && (
                  <div>
                    <h4>Body Parameters</h4>
                    {bodyKeys.map((key) => (
                      <label key={`b-${key}`}>
                        {key}
                        <input
                          value={bodyParams[key]}
                          onChange={(e) =>
                            setBodyParams((p) => ({
                              ...p,
                              [key]: e.target.value,
                            }))
                          }
                          placeholder={`Value for {{${key}}}`}
                        />
                      </label>
                    ))}
                  </div>
                )}

                {buttonKeys.length > 0 && (
                  <div>
                    <h4>Button Parameters</h4>
                    {buttonKeys.map((key) => (
                      <label key={`btn-${key}`}>
                        {key}
                        <input
                          value={buttonParams[key]}
                          onChange={(e) =>
                            setButtonParams((p) => ({
                              ...p,
                              [key]: e.target.value,
                            }))
                          }
                        />
                      </label>
                    ))}
                  </div>
                )}

                {selectedTemplate.components?.some(
                  (c) => c.type === "HEADER" && c.format !== "TEXT",
                ) && (
                  <label>
                    Media URL
                    <input
                      value={mediaUrl}
                      onChange={(e) => setMediaUrl(e.target.value)}
                      placeholder="Template media URL"
                    />
                  </label>
                )}
              </div>
            )}

            {selectedTemplate?.category === "LOCATION" && (
              <div className="sendtest-location">
                <h4>Location</h4>
                <label>
                  Latitude
                  <input
                    name="latitude"
                    value={location.latitude}
                    onChange={(e) =>
                      setLocation((l) => ({
                        ...l,
                        [e.target.name]: e.target.value,
                      }))
                    }
                  />
                </label>
                <label>
                  Longitude
                  <input
                    name="longitude"
                    value={location.longitude}
                    onChange={(e) =>
                      setLocation((l) => ({
                        ...l,
                        [e.target.name]: e.target.value,
                      }))
                    }
                  />
                </label>
                <label>
                  Location Name
                  <input
                    name="locationName"
                    value={location.locationName}
                    onChange={(e) =>
                      setLocation((l) => ({
                        ...l,
                        [e.target.name]: e.target.value,
                      }))
                    }
                  />
                </label>
                <label>
                  Address
                  <input
                    name="locationAddress"
                    value={location.locationAddress}
                    onChange={(e) =>
                      setLocation((l) => ({
                        ...l,
                        [e.target.name]: e.target.value,
                      }))
                    }
                  />
                </label>
              </div>
            )}

            <div className="sendtest-field">
              <label>Recipient mobile</label>
              <div className="sendtest-phone-row">
                <select
                  value={country.iso}
                  onChange={(e) => {
                    const next = COUNTRIES.find((c) => c.iso === e.target.value);
                    if (next) setCountry(next);
                  }}
                  aria-label="Country code"
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.iso} value={c.iso}>
                      {c.name} ({c.code})
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  placeholder="10-digit mobile number"
                  value={mobile}
                  onChange={(e) =>
                    setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))
                  }
                  required
                  inputMode="numeric"
                  maxLength={10}
                  pattern="[0-9]{10}"
                  title="Enter a 10-digit mobile number"
                />
              </div>
            </div>

            <div className="sendtest-actions">
              <button
                type="submit"
                className="sendtest-send-btn"
                disabled={
                  sending ||
                  (channel === "whatsapp" && (projectLoading || needsWhatsAppSetup))
                }
              >
                <FaPaperPlane />
                {sending ? "Sending…" : "Send test"}
              </button>
            </div>
            {apiResponse?.message && (
              <p
                className={`sendtest-api-response${
                  apiResponse.success === false ? " sendtest-api-response-error" : ""
                }`}
              >
                {apiResponse.message}
              </p>
            )}
          </form>
        </section>

        <aside className="sendtest-preview">
          <div className="sendtest-preview-head">
            <h2>Preview</h2>
            <span>{channel === "sms" ? "SMS" : "WhatsApp"}</span>
          </div>
          <div className="sendtest-preview-stage">
            {selectedTemplate ? (
              <div className="sendtest-wa-bubble">
                {headerPreview && (
                  <p className="sendtest-preview-title">{headerPreview}</p>
                )}
                <p className="sendtest-preview-body">{previewText()}</p>
                {buttonsPreview.length > 0 && (
                  <div className="sendtest-preview-buttons">
                    {buttonsPreview.map((btn, idx) => (
                      <span key={btn.text || idx}>{btn.text || "Button"}</span>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="sendtest-preview-empty">
                <span className="sendtest-preview-empty-icon">
                  <FaComments />
                </span>
                <strong>No template selected</strong>
                <span>Pick a template to preview the message here.</span>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
};

export default SendTestMessage;
